import { Box, CircularProgress, Typography } from "@mui/material";
import { useState } from "react";

import {
  RULE_GRAPH_VIEWPORT_CONTROL_HEIGHT,
  ruleGraphCanvasControlSx,
} from "./RuleGraphCanvasChrome";

/**
 * Admin-only: re-derive the graph from the saved draft source. Renders
 * nothing for everyone else. Purely presentational — the mutation pair
 * lives in {@link useRegenerateGraph}, owned by the page, so the canvas can
 * shimmer off the same busy signal this button spins on.
 *
 * Wears the shared canvas-pill surface so it reads as one family with the
 * other floating viewport controls (zoom, back to start).
 */
export const RegenerateGraphButton = ({
  isBusy,
  canRegenerate,
  onRegenerate,
}: {
  isBusy: boolean;
  canRegenerate: boolean;
  onRegenerate: () => void;
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const showsHoverState = isHovered && !isBusy;

  if (!canRegenerate) return null;

  return (
    <Box
      component="button"
      disabled={isBusy}
      onClick={onRegenerate}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={(theme) => ({
        ...ruleGraphCanvasControlSx(isBusy)(theme),
        gap: 1.5,
        height: RULE_GRAPH_VIEWPORT_CONTROL_HEIGHT,
        px: 3,
        py: 1.5,
        "&:hover:not(:disabled)": {
          borderColor: theme.igBackgroundColors.interactive.dark.disabled,
        },
        "&:active:not(:disabled)": {
          backgroundColor: theme.igBackgroundColors.interactive.neutral.active,
        },
      })}
      type="button"
    >
      {isBusy ? (
        <CircularProgress
          data-testid="rule-graph-regenerate-spinner"
          size={12}
          sx={(theme) => ({ color: theme.palette.text.tertiary })}
        />
      ) : null}
      <Typography
        component="span"
        sx={(theme) => ({
          color: showsHoverState
            ? theme.palette.text.primary
            : theme.palette.text.tertiary,
          fontSize: 12,
          lineHeight: 1.5,
          whiteSpace: "nowrap",
        })}
      >
        {isBusy ? "Regenerating graph…" : "Regenerate graph"}
      </Typography>
    </Box>
  );
};
